const THEME_RULES = [
  { id: 'prazo', label: 'Prazo', aliases: ['prazo', 'cronograma', 'vigência', 'vigencia', 'implantação', 'implantacao', 'produção', 'producao', 'homologação', 'homologacao'] },
  { id: 'schema', label: 'Schema', aliases: ['schema', 'xsd', 'xml', 'layout', 'campo', 'tag'] },
  { id: 'regras', label: 'Regras', aliases: ['regra', 'validação', 'validacao', 'rejeição', 'rejeicao', 'denegação', 'denegacao', 'critério', 'criterio'] },
  { id: 'contingencia', label: 'Contingência', aliases: ['contingência', 'contingencia', 'svrs', 'svc', 'offline', 'indisponibilidade'] },
  { id: 'manual', label: 'Manual', aliases: ['manual', 'guia', 'orientação', 'orientacao', 'orientações', 'orientacoes', 'procedimento'] },
  { id: 'nt', label: 'Nota Técnica', aliases: ['nota técnica', 'nota tecnica', 'nt ', 'nt-', 'nt_'] },
  { id: 'evento', label: 'Evento', aliases: ['evento', 'cancelamento', 'manifestação', 'manifestacao', 'cce', 'carta de correção', 'carta de correcao'] }
];

const IMPACT_RULES = [
  {
    level: 'alta',
    score: 3,
    patterns: ['prazo', 'obrigator', 'implant', 'producao', 'rejei', 'schema', 'layout', 'valida', 'indispon', 'conting']
  },
  {
    level: 'media',
    score: 2,
    patterns: ['manual', 'orienta', 'ajuste', 'atualiza', 'evento', 'versao', 'versao']
  }
];

function normalizeText(value = '') {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function buildDocumentText(document) {
  return normalizeText([
    document.nome,
    document.description,
    document.tipo,
    document.portal,
    document.sistema
  ].filter(Boolean).join(' '));
}

function parseDocumentDate(value) {
  if (!value) return null;

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [day, month, year] = value.split('/').map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveDocumentDate(document) {
  return (
    parseDocumentDate(document.data) ||
    parseDocumentDate(document.firstSeenAt) ||
    parseDocumentDate(document.createdAt) ||
    parseDocumentDate(document.updatedAt) ||
    null
  );
}

function formatRelativeDays(date) {
  if (!date) return 'Sem data';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compare = new Date(date);
  compare.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - compare) / 86400000);

  if (diffDays <= 0) return 'Hoje';
  if (diffDays === 1) return 'Ontem';
  if (diffDays < 7) return `${diffDays} dias atrás`;
  return compare.toLocaleDateString('pt-BR');
}

function extractThemeTags(documentText) {
  return THEME_RULES.filter((rule) => rule.aliases.some((alias) => documentText.includes(alias))).map((rule) => rule.label);
}

function detectImpactLevel(documentText) {
  for (const rule of IMPACT_RULES) {
    if (rule.patterns.some((pattern) => documentText.includes(pattern))) {
      return rule.level;
    }
  }

  return 'baixa';
}

function buildActionSuggestion(tags, impactLevel) {
  if (tags.includes('Prazo')) return 'Validar cronograma interno e comunicar clientes impactados.';
  if (tags.includes('Schema')) return 'Revisar XML, validações e testes de integração.';
  if (tags.includes('Regras')) return 'Checar regras de negócio e possíveis rejeições novas.';
  if (tags.includes('Contingência')) return 'Monitorar disponibilidade e plano de contingência.';
  if (impactLevel === 'alta') return 'Priorizar leitura completa e abrir avaliação técnica.';
  if (impactLevel === 'media') return 'Registrar no backlog fiscal e acompanhar próximos desdobramentos.';
  return 'Manter no radar para consulta futura.';
}

function buildInsightSummary(document) {
  const description = String(document.description || '').trim();
  if (description) {
    return description.split(/(?<=[.!?])\s+/)[0].slice(0, 180);
  }

  if (document.tipo && document.nome) {
    return `${document.tipo}: ${document.nome}`;
  }

  return document.nome || 'Documento sem resumo disponível.';
}

function normalizeSeriesName(name = '') {
  return String(name)
    .replace(/\b(v|versao|versão)\.?\s*\d+([.,]\d+)*\b/gi, '')
    .replace(/\b\d{2}\/\d{2}\/\d{4}\b/g, '')
    .replace(/[()\-_:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractSeriesKey(document) {
  const rawName = String(document?.nome || '');
  const normalizedName = normalizeText(rawName);

  const ntMatch = normalizedName.match(/\b(?:nt|nota tecnica)\s*(\d{4}\.\d{3})\b/i);
  if (ntMatch) {
    return `NT ${ntMatch[1]}`;
  }

  const genericNumberedMatch = normalizedName.match(/\b(\d{4}\.\d{3})\b/);
  if (genericNumberedMatch) {
    const prefixMatch = normalizedName.match(/\b([a-z]{2,12})\s*\d{4}\.\d{3}\b/);
    if (prefixMatch) {
      return `${prefixMatch[1].toUpperCase()} ${genericNumberedMatch[1]}`;
    }
  }

  const normalizedSeriesName = normalizeSeriesName(rawName);
  return normalizedSeriesName.length >= 8 ? normalizedSeriesName : '';
}

function getDocumentComparisonKind(document = {}) {
  const documentText = buildDocumentText(document);
  const isSchemaPackage = (
    /\bpacotes?\s+de\s+schemas?\b/.test(documentText) ||
    (documentText.includes('pacote') && /\bschemas?\b/.test(documentText))
  );

  if (isSchemaPackage) return 'pacote-schema';

  const isTechnicalNote = (
    /\bnota\s+tecnica\b/.test(documentText) ||
    /\bnt\s*\d{4}\.\d{3}\b/.test(documentText)
  );

  if (isTechnicalNote) return 'nota-tecnica';
  if (/\bschemas?\b|\bxsd\b/.test(documentText)) return 'schema';
  return 'documento';
}

function tokenize(value = '') {
  return normalizeText(value)
    .split(/[^a-z0-9]+/g)
    .filter((token) => token.length > 2);
}

function diffHighlights(currentDocument, previousDocument) {
  const currentTokens = new Set(tokenize(`${currentDocument.nome} ${currentDocument.description || ''}`));
  const previousTokens = new Set(tokenize(`${previousDocument.nome} ${previousDocument.description || ''}`));

  return Array.from(currentTokens).filter((token) => !previousTokens.has(token)).slice(0, 6);
}

function buildDocumentInsights(documents = []) {
  return documents
    .map((document) => {
      const searchableText = buildDocumentText(document);
      const tags = extractThemeTags(searchableText);
      const impactLevel = detectImpactLevel(searchableText);
      const resolvedDate = resolveDocumentDate(document);

      return {
        ...document,
        actionSuggestion: buildActionSuggestion(tags, impactLevel),
        impactLevel,
        relativeDateLabel: formatRelativeDays(resolvedDate),
        resolvedDate,
        searchableText,
        summary: buildInsightSummary(document),
        tags
      };
    })
    .sort((left, right) => {
      const leftTime = left.resolvedDate ? left.resolvedDate.getTime() : 0;
      const rightTime = right.resolvedDate ? right.resolvedDate.getTime() : 0;
      return rightTime - leftTime;
    });
}

function buildImpactSummary(insights = []) {
  return insights.reduce((accumulator, insight) => {
    accumulator.total += 1;
    accumulator[insight.impactLevel] += 1;
    return accumulator;
  }, {
    total: 0,
    alta: 0,
    media: 0,
    baixa: 0
  });
}

function buildDigest(insights = []) {
  const now = Date.now();
  const windows = [
    { id: '24h', label: '24h', maxAge: 86400000 },
    { id: '7d', label: '7 dias', maxAge: 7 * 86400000 },
    { id: '15d', label: '15 dias', maxAge: 15 * 86400000 }
  ];

  return windows.map((window) => {
    const items = insights.filter((insight) => {
      if (!insight.resolvedDate) return false;
      return now - insight.resolvedDate.getTime() <= window.maxAge;
    });

    const impactSummary = buildImpactSummary(items);

    return {
      ...window,
      items,
      impactSummary,
      topTags: summarizeTags(items)
    };
  });
}

function summarizeTags(insights = []) {
  const counts = new Map();
  insights.forEach((insight) => {
    insight.tags.forEach((tag) => {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    });
  });

  return Array.from(counts.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map(([label, count]) => ({ label, count }));
}

function buildTimeline(insights = []) {
  return insights.slice(0, 8).map((insight) => ({
    id: insight.id || insight.url,
    impactLevel: insight.impactLevel,
    portal: insight.portal || insight.sistema || 'Portal',
    summary: insight.summary,
    title: insight.nome,
    when: insight.relativeDateLabel,
    url: insight.url
  }));
}

function buildVersionComparisons(insights = []) {
  const groups = insights.reduce((accumulator, insight) => {
    const seriesName = extractSeriesKey(insight);
    if (!seriesName || seriesName.length < 8) return accumulator;

    const comparisonKind = getDocumentComparisonKind(insight);
    const groupKey = `${comparisonKind}::${seriesName}`;
    if (!accumulator[groupKey]) {
      accumulator[groupKey] = {
        comparisonKind,
        items: [],
        seriesName
      };
    }

    accumulator[groupKey].items.push(insight);
    return accumulator;
  }, {});

  return Object.values(groups)
    .map(({ seriesName, comparisonKind, items }) => {
      const sorted = [...items].sort((left, right) => {
        const leftTime = left.resolvedDate ? left.resolvedDate.getTime() : 0;
        const rightTime = right.resolvedDate ? right.resolvedDate.getTime() : 0;
        return rightTime - leftTime;
      });

      if (sorted.length < 2) return null;

      return {
        comparisonKind,
        seriesName,
        current: sorted[0],
        previous: sorted[1],
        older: sorted.slice(2, 5),
        highlights: diffHighlights(sorted[0], sorted[1])
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

function buildWatchlist(insights = []) {
  return THEME_RULES.map((rule) => {
    const count = insights.filter((insight) => insight.tags.includes(rule.label)).length;
    return {
      id: rule.id,
      label: rule.label,
      query: rule.aliases[0],
      count
    };
  }).filter((item) => item.count > 0);
}

function expandSemanticQuery(query = '') {
  const normalized = normalizeText(query);
  if (!normalized) return [];

  const tokens = normalized.split(/\s+/).filter(Boolean);
  const semanticAliases = new Set(tokens);

  THEME_RULES.forEach((rule) => {
    const hasMatch = rule.aliases.some((alias) => normalized.includes(alias)) || normalized.includes(normalizeText(rule.label));
    if (hasMatch) {
      rule.aliases.forEach((alias) => semanticAliases.add(alias.trim()));
      semanticAliases.add(normalizeText(rule.label));
    }
  });

  return Array.from(semanticAliases).filter(Boolean);
}

function filterDocumentsByQuery(documents = [], query = '') {
  const normalizedQuery = normalizeText(query).trim();
  if (!normalizedQuery) return documents;

  const aliases = expandSemanticQuery(query);

  return documents.filter((document) => {
    const haystack = buildDocumentText(document);
    return aliases.some((alias) => haystack.includes(alias));
  });
}

export {
  buildDigest,
  buildDocumentInsights,
  buildImpactSummary,
  buildTimeline,
  buildVersionComparisons,
  buildWatchlist,
  extractSeriesKey,
  filterDocumentsByQuery,
  getDocumentComparisonKind,
  resolveDocumentDate
};
